import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Card } from "./ui/card";
import { Landmark, ArrowDownToLine, Clock, CheckCircle2, XCircle, Settings } from "lucide-react";
import { Switch } from "./ui/switch";
import { toast } from "sonner";

const fmt = (n) => parseFloat(n || 0).toFixed(2).replace(".", ",");

export default function PayoutPanel({ company, onRefresh }) {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  // Auto-payout config
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoCfg, setAutoCfg] = useState({
    enabled: !!company.auto_payout_enabled,
    min: company.auto_payout_min_amount ?? 100,
    day: company.auto_payout_day_of_month ?? "",
  });

  const saveAuto = async () => {
    try {
      await api.patch(`/payouts/auto-config/${company.id}`, {
        auto_payout_enabled: !!autoCfg.enabled,
        auto_payout_min_amount: parseFloat(autoCfg.min) || 0,
        auto_payout_day_of_month: autoCfg.day === "" ? null : parseInt(autoCfg.day),
      });
      toast.success("Configuração salva");
      setAutoOpen(false);
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  const load = () => api.get(`/payouts/summary/${company.id}`).then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { load(); }, [company.id]);

  const submit = async () => {
    const v = parseFloat(amount);
    if (!v || v <= 0) { toast.error("Informe um valor válido"); return; }
    if (!summary?.pix_key) { toast.error("Cadastre uma chave PIX primeiro"); return; }
    if (!window.confirm(`Solicitar resgate de B$ ${fmt(v)} para a chave PIX ${summary.pix_key}?`)) return;
    setBusy(true);
    try {
      await api.post("/payouts/request", { company_id: company.id, amount: v });
      toast.success("Solicitação enviada! Aguarde aprovação do administrador.");
      setOpen(false); setAmount("");
      load();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
    finally { setBusy(false); }
  };

  if (!summary) return null;

  return (
    <Card className="p-4 mt-3 rounded-2xl border-blue-200 bg-blue-50/40" data-testid={`payout-panel-${company.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-blue-900 font-bold text-sm"><Landmark size={16}/>Saldo a Receber</div>
          <div className="text-xs text-stone-600 mt-0.5">PIX: {summary.pix_key ? <code className="font-mono">{summary.pix_key}</code> : <span className="text-red-700 font-bold">cadastre uma chave PIX no perfil da empresa</span>}</div>
          {summary.pending_count > 0 && <div className="text-xs text-amber-700 mt-1 font-bold flex items-center gap-1"><Clock size={12}/>{summary.pending_count} solicitação{summary.pending_count > 1 ? "ões" : ""} pendente{summary.pending_count > 1 ? "s" : ""} (B$ {fmt(summary.pending_total)})</div>}
        </div>
        <Button onClick={() => setOpen(true)} disabled={!summary.pix_key || summary.available <= 0} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid={`payout-btn-${company.id}`}>
          <ArrowDownToLine size={14} className="mr-1"/>Resgatar via PIX
        </Button>
      </div>

      {/* Auto-payout summary + toggle */}
      <div className="mt-3 pt-3 border-t border-stone-200 flex items-center justify-between gap-3 flex-wrap" data-testid={`autopayout-${company.id}`}>
        <div className="text-xs">
          <span className="text-stone-500">Auto-payout: </span>
          {company.auto_payout_enabled ? (
            <span className="text-emerald-700 font-bold">Ativado{company.auto_payout_min_amount > 0 && ` quando saldo ≥ B$ ${fmt(company.auto_payout_min_amount)}`}{company.auto_payout_day_of_month && ` · todo dia ${company.auto_payout_day_of_month}`}</span>
          ) : (
            <span className="text-stone-500">Desativado</span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setAutoOpen(true)} className="rounded-xl text-xs" data-testid={`autopayout-config-${company.id}`}>
          <Settings size={12} className="mr-1"/>Configurar
        </Button>
      </div>

      {summary.recent_paid?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-stone-200">
          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Últimos pagos</div>
          <div className="space-y-1">
            {summary.recent_paid.slice(0, 3).map((p) => (
              <div key={p.id} className="text-xs flex items-center justify-between" data-testid={`paid-${p.id}`}>
                <span className="text-stone-600 flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600"/>{new Date(p.paid_at).toLocaleDateString("pt-BR")}</span>
                <strong>B$ {fmt(p.amount)}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
      {summary.pending?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-stone-200 space-y-1">
          {summary.pending.map((p) => (
            <div key={p.id} className="text-xs flex items-center justify-between" data-testid={`pending-${p.id}`}>
              <span className="text-amber-700 flex items-center gap-1"><Clock size={11}/>Aguardando — {new Date(p.created_at).toLocaleDateString("pt-BR")}</span>
              <strong>B$ {fmt(p.amount)}</strong>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2"><ArrowDownToLine size={20}/>Resgatar via PIX</DialogTitle>
              <DialogDescription>O valor será transferido para a chave PIX cadastrada da empresa em até 1 dia útil após aprovação.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-stone-50 border border-stone-200 text-sm">
                <div>Saldo disponível: <strong>B$ {fmt(summary.available)}</strong></div>
                <div>Chave PIX: <code className="font-mono text-xs">{summary.pix_key}</code></div>
              </div>
              <div><Label>Valor a resgatar (B$)</Label><Input type="number" step="0.01" min="0.01" max={summary.available} value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl mt-1" data-testid="payout-amount"/></div>
              <Button type="button" onClick={() => setAmount(summary.available.toFixed(2))} variant="outline" size="sm" className="text-xs rounded-xl">Usar saldo total</Button>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button onClick={submit} disabled={busy || !amount} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="payout-confirm">
                {busy ? "Enviando..." : "Solicitar resgate"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {autoOpen && (
        <Dialog open onOpenChange={(v) => !v && setAutoOpen(false)}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2"><Settings size={20}/>Auto-payout PIX</DialogTitle>
              <DialogDescription>Configure resgates automáticos. O sistema cria a solicitação para você; o administrador ainda aprova manualmente.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="flex items-center gap-2"><Switch checked={!!autoCfg.enabled} onCheckedChange={(v) => setAutoCfg({...autoCfg, enabled: v})} data-testid="autopayout-enable"/><span className="text-sm font-medium">Ativar auto-payout</span></label>
              <div><Label>Sacar quando saldo for ≥ (B$)</Label><Input type="number" min="0" step="0.01" value={autoCfg.min} onChange={(e) => setAutoCfg({...autoCfg, min: e.target.value})} className="rounded-xl mt-1" data-testid="autopayout-min"/><p className="text-[11px] text-stone-500 mt-1">Use 0 para desativar este gatilho.</p></div>
              <div><Label>OU todo dia X do mês (1-28)</Label><Input type="number" min="1" max="28" value={autoCfg.day} onChange={(e) => setAutoCfg({...autoCfg, day: e.target.value})} className="rounded-xl mt-1 w-24" placeholder="" data-testid="autopayout-day"/><p className="text-[11px] text-stone-500 mt-1">Deixe vazio para não sacar por data.</p></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setAutoOpen(false)} className="rounded-xl">Cancelar</Button>
              <Button onClick={saveAuto} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="autopayout-save">Salvar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
